import { useEffect, useState,  Dispatch, SetStateAction } from "react";
import { gql, useMutation } from "@apollo/client";

import { Text } from "../../Type";
import TextLink from "../../TextLinks";
import { CircleChecked } from "../../Icons/general-interface-2";
import styles from "./index.module.scss";
import { FileType } from "../../../types/fileType";
import Modal from "../../../components/Modal";
import ModalActions from "../../../components/ModalActions";
import Button from "../../../components/Button";

const GET_CLIENT_FILE = gql`
  query GetClientFile($id: ID!) {
    clientFile(id: $id) {
      id
      wet_signature {
        name
        url
      }
    }
  }
`;

const UPLOAD_FILE = gql`
  mutation ($file: Upload!) {
    upload(file: $file) {
      id
      name
      url
    }
  }
`;

const LINK_DOCUMENT = gql`
  mutation LinkDocument($input: updateClientFileInput) {
    updateClientFile(input: $input) {
      clientFile {
        id
        wet_signature {
          name
          url
        }
      }
    }
  }
`;

const DELETE_UPLOAD_FILE = gql`
  mutation DeleteUploadFile($input: DeleteUploadFileInput) {
    deleteUploadFile(input: $input) {
      message
    }
  }
`;

type PropsType = {
  file: FileType;
  fieldName: string;
  clientFileId: string;
  setHasFile: Dispatch<SetStateAction<boolean>>;
  disabled: boolean;
};

const FileContainer = ({ file, fieldName, clientFileId, setHasFile, disabled }: PropsType) => {
  const propFileName = file?.name || "";
  const propFileUrl = file?.url || "";
  const [fileName, setFileName] = useState(propFileName);
  const [fileUrl, setFileUrl] = useState(propFileUrl);
  const id = `upload-modal-${fieldName}`;
  const [message, setMessage] = useState<string>();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (propFileName !== fileName) {
      setFileName(propFileName);
    }
    if (propFileUrl !== fileUrl) {
      setFileUrl(propFileUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propFileName, propFileUrl]);

  const [linkFile, { error: errorLink, loading: loadingLink }] = useMutation(
    LINK_DOCUMENT,
    {
      refetchQueries: [GET_CLIENT_FILE],
      awaitRefetchQueries: true,
      onCompleted: (i) => {
        const name = i.updateClientFile.clientFile?.[fieldName]?.name;
        const url = i.updateClientFile.clientFile?.[fieldName]?.url;
        setFileName(name || "");
        setFileUrl(url || "");
        setHasFile(true);
      },
      onError: () => {
        setMessage("Error while uploading file");
        return null;
      },
    }
  );

  const [uploadFile, { error: errorUpload, loading: loadingUpload }] =
    useMutation(UPLOAD_FILE, {
      onCompleted: (i) => {
        linkFile({
          variables: {
            input: {
              where: {
                id: clientFileId,
              },
              data: {
                [fieldName]: i?.upload.id,
              },
            },
          },
        });
      },
      onError: () => {
        setMessage("Error while uploading file");
        return null;
      },
    });

  const [deleteUploadFile, { error: errorDelete, loading: loadingDelete }] = useMutation(DELETE_UPLOAD_FILE, {
    onCompleted: (data) => {
      if (data.deleteUploadFile.message === "success") {
        setMessage("Successfully deleted");
        setFileName("");
        setFileUrl("");
        setHasFile(false);
        return null;
      };
      setMessage("Error while deleting file");
      return null;
    },
    onError: () => {
      setMessage("Error while deleting file");
      return null;
    },
  });

  const status =
    !errorUpload &&
    !errorLink &&
    !errorDelete &&
    !loadingUpload &&
    !loadingLink &&
    !loadingDelete;

    return (
    <>
      {fileName && status && (
        <>
          <div className={styles.fileLinkContainer}>
            <TextLink
              to={`${fileUrl}`}
              externalURL
              className={styles.textLink}
              disabled={disabled}
            >
              {fileName}
            </TextLink>
            <label
              id={`${id}-delete-label`}
              htmlFor={`${id}-delete`}
              className={styles.delete}
            >
              <Text
                size="medium"
                weight="bold"
                className={styles.uploadText}
              >
                <span>Delete</span>
              </Text>
              {fileName && (
                <input
                  type="button"
                  id={`${id}-delete`}
                  className={styles.hiddenInput}
                  onClick={() => setDeleteModalOpen(true)}
                  disabled={disabled}
                />
              )}
            </label>
          </div>
          {message && (
            <span>
              <span className={styles.successText}>
                <CircleChecked className={styles.icon} />
                {message}
              </span>
            </span>
          )}
        </>
      )}
      <label id={`${id}-upload-label`} htmlFor={`${id}-upload`}>
        <Text size="medium" weight="bold" className={styles.uploadText}>
          <span>
            {errorUpload
              ? "Error while uploading file"
              : errorLink
              ? "Error while linking file"
              : errorDelete
              ? "Error while deleting file"
              : loadingUpload
              ? "Uploading File"
              : loadingLink
              ? "Linking File"
              : loadingDelete
              ? "Deleting File"
              : !fileName
              ? "Upload file"
              : ""}
          </span>
        </Text>
        {!fileName && (
          <input
            type="file"
            id={`${id}-upload`}
            className={styles.hiddenInput}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.files?.item(0)) {
                setMessage(undefined);
                const uploadedFile = e.target.files?.item(0);
                uploadFile({
                  variables: {
                    file: uploadedFile,
                  },
                });
              }
            }}
          />
        )}
      </label>

      <Modal
        isOpen={isDeleteModalOpen}
        title="Delete Supporting Document"
        onClose={() => { setDeleteModalOpen(false); }}
      >
        <div>
          <Text style={{ fontSize: "20px", lineHeight: "36px" }}>
            Are you sure you want to delete &quot;{fileName}&quot;?
          </Text>
        </div>
        <ModalActions>
          <Button
            primary
            onClick={() => {
              setDeleteModalOpen(false);
              setMessage(undefined);
              deleteUploadFile({
                variables: {
                  input: {
                    clientFileId,
                    fieldName,
                  },
                },
              });
            }}
          >
            Delete
          </Button>
          <Button
            secondary
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default FileContainer;
